import * as React from 'react';

const HIGHLIGHT_BG = 'rgba(247, 201, 72, 0.55)';

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function normalizeHighlightTerms(raw?: string | null): string[] {
  const v = (raw ?? '').trim();
  if (!v) return [];

  const tokens = v
    .split(/\s+/g)
    .map(t => t.trim())
    .filter(t => t.length >= 2);

  // Keep order, unique (case-insensitive)
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of [v, ...tokens]) {
    const key = t.toLowerCase();
    if (!t) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

export function clearHighlights(root: HTMLElement) {
  const nodes = root.querySelectorAll('[data-preview-highlight="1"]');
  nodes.forEach(node => {
    const parent = node.parentNode;
    if (!parent) return;
    // Replace the highlight wrapper with its text.
    const text = document.createTextNode(node.textContent || '');
    parent.replaceChild(text, node);
    // Merge adjacent text nodes if needed.
    parent.normalize();
  });
}

export function applyHighlightsToElement(root: HTMLElement, rawTerms?: string | null) {
  const terms = normalizeHighlightTerms(rawTerms);
  clearHighlights(root);
  if (!terms.length) return;

  const splitRe = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi');
  const testRe = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'i');

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      const tag = parent.tagName?.toLowerCase();
      if (tag === 'script' || tag === 'style') return NodeFilter.FILTER_REJECT;
      if (parent.getAttribute('data-preview-highlight') === '1') return NodeFilter.FILTER_REJECT;
      if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
      if (!testRe.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    }
  } as any);

  const toProcess: Text[] = [];
  while (walker.nextNode()) {
    toProcess.push(walker.currentNode as Text);
  }

  for (const textNode of toProcess) {
    const text = textNode.nodeValue || '';
    const parts = text.split(splitRe);
    if (parts.length <= 1) continue;

    const frag = document.createDocumentFragment();
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i] || '';
      if (!part) continue;

      // Odd indexes correspond to capture groups from split()
      if (i % 2 === 1) {
        const mark = document.createElement('span');
        mark.setAttribute('data-preview-highlight', '1');
        mark.style.background = HIGHLIGHT_BG;
        mark.style.borderRadius = '2px';
        mark.style.padding = '0 1px';
        mark.textContent = part;
        frag.appendChild(mark);
      } else {
        frag.appendChild(document.createTextNode(part));
      }
    }

    const parent = textNode.parentNode;
    if (!parent) continue;
    parent.replaceChild(frag, textNode);
  }
}

export function renderHighlightedPlainText(text: string, rawTerms?: string | null): React.ReactNode {
  const terms = normalizeHighlightTerms(rawTerms);
  if (!terms.length) return text;

  const re = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi');
  const parts = (text ?? '').split(re);

  return parts.map((p, idx) => {
    if (!p) return null;
    if (idx % 2 === 1) {
      return React.createElement(
        'span',
        {
          key: idx,
          'data-preview-highlight': '1',
          style: { background: HIGHLIGHT_BG, borderRadius: 2, padding: '0 1px' }
        },
        p
      );
    }
    return p;
  });
}
